import React, { useState } from 'react';
import { Phone, Mail, Instagram, MapPin, Clock, Send, Star, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { collections, featuredProducts, testimonials, shopInfo } from '../data/mock';

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });
    setFormData({ name: '', email: '', message: '' });
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${shopInfo.whatsapp.replace(/[^0-9]/g, '')}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#faf8f6]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#d4c5b9]/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_0f087157-7047-42d2-963e-36ed51ca707e/artifacts/qd2g138c_Shop%20Cover%20Photo.jpeg" 
                alt="Zaya Logo" 
                className="h-14 w-14 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-[#8b7355]">Zaya</h1>
                <p className="text-xs text-[#a89580]">{shopInfo.tagline}</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#home" className="text-[#6b5d52] hover:text-[#8b7355] transition-colors">Home</a>
              <a href="#collections" className="text-[#6b5d52] hover:text-[#8b7355] transition-colors">Collections</a>
              <a href="#about" className="text-[#6b5d52] hover:text-[#8b7355] transition-colors">About</a>
              <a href="#contact" className="text-[#6b5d52] hover:text-[#8b7355] transition-colors">Contact</a>
              <Button 
                onClick={handleWhatsApp}
                className="bg-[#8b7355] hover:bg-[#6b5d52] text-white"
              >
                <Phone className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="px-4 py-2 bg-[#f4e4d7] text-[#8b7355] rounded-full text-sm font-medium">
                  New Collection 2026
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-[#6b5d52] leading-tight">
                Embrace<br />
                <span className="text-[#c9a68a]">Elegance</span> &<br />
                Modesty
              </h1>
              <p className="text-lg text-[#8b7968] leading-relaxed">
                Discover our exquisite collection of abayas and hijabs that celebrate timeless beauty and contemporary style.
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={handleWhatsApp}
                  size="lg" 
                  className="bg-[#8b7355] hover:bg-[#6b5d52] text-white px-8"
                >
                  Shop Now
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-[#c9a68a] text-[#8b7355] hover:bg-[#f4e4d7]"
                  onClick={() => document.getElementById('collections').scrollIntoView({ behavior: 'smooth' })}
                >
                  View Collections
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#f4e4d7]/40 to-transparent rounded-3xl transform rotate-6"></div>
              <img 
                src="https://images.unsplash.com/photo-1767469697194-ac997d70b1ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwYWJheWF8ZW58MHx8fHwxNzcwNzMwNzM3fDA&ixlib=rb-4.1.0&q=85"
                alt="Elegant Abaya"
                className="relative rounded-3xl shadow-2xl w-full h-[600px] object-cover"
              />
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="bg-[#f4e4d7] p-3 rounded-full">
                    <ShoppingBag className="w-6 h-6 text-[#8b7355]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#6b5d52]">100+</p>
                    <p className="text-sm text-[#8b7968]">Unique Designs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#6b5d52] mb-4">Featured Collection</h2>
            <p className="text-[#8b7968] max-w-2xl mx-auto">
              Handpicked pieces that embody our commitment to elegance and quality
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map((product) => (
              <Card key={product.id} className="group overflow-hidden border-[#e8ddd2] hover:shadow-xl transition-all duration-300">
                <div className="relative overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {product.new && (
                    <span className="absolute top-4 left-4 bg-[#8b7355] text-white px-3 py-1 rounded-full text-xs font-medium">
                      New
                    </span>
                  )}
                  {product.featured && (
                    <span className="absolute top-4 left-4 bg-[#c9a68a] text-white px-3 py-1 rounded-full text-xs font-medium">
                      Featured
                    </span>
                  )}
                </div>
                <CardContent className="p-6">
                  <p className="text-xs text-[#a89580] uppercase tracking-wider mb-2">{product.category}</p>
                  <h3 className="text-lg font-semibold text-[#6b5d52] mb-2">{product.name}</h3>
                  <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-[#8b7355]">{product.price}</span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleWhatsApp}
                      className="border-[#c9a68a] text-[#8b7355] hover:bg-[#f4e4d7]"
                    >
                      Inquire
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Collections */}
      <section id="collections" className="py-16 px-4 bg-[#faf8f6]">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#6b5d52] mb-4">Our Collections</h2>
            <p className="text-[#8b7968] max-w-2xl mx-auto">
              Explore our carefully curated collections designed for every occasion
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {collections.map((collection) => (
              <Card key={collection.id} className="group overflow-hidden border-[#e8ddd2] hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="relative overflow-hidden h-96">
                  <img 
                    src={collection.image} 
                    alt={collection.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#6b5d52]/90 via-[#6b5d52]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-bold mb-2">{collection.name}</h3>
                    <p className="text-sm mb-4 opacity-90">{collection.description}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{collection.items} Items</span>
                      <span className="text-sm">•</span>
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={handleWhatsApp}
                        className="bg-white text-[#6b5d52] hover:bg-[#f4e4d7]"
                      >
                        View Collection
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-[#6b5d52]">About Zaya</h2>
              <p className="text-[#8b7968] leading-relaxed">
                {shopInfo.description}
              </p>
              <p className="text-[#8b7968] leading-relaxed">
                At Zaya, we believe that modest fashion should never compromise on style or quality. Our artisans craft each piece with meticulous attention to detail, using premium fabrics and timeless designs that make you feel confident and beautiful.
              </p>
              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="text-center p-6 bg-[#faf8f6] rounded-xl">
                  <p className="text-3xl font-bold text-[#8b7355] mb-2">100+</p>
                  <p className="text-sm text-[#8b7968]">Unique Designs</p>
                </div>
                <div className="text-center p-6 bg-[#faf8f6] rounded-xl">
                  <p className="text-3xl font-bold text-[#8b7355] mb-2">1000+</p>
                  <p className="text-sm text-[#8b7968]">Happy Customers</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://images.unsplash.com/photo-1760083545495-b297b1690672?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwYWJheWF8ZW58MHx8fHwxNzcwNzMwNzM3fDA&ixlib=rb-4.1.0&q=85"
                alt="Abaya Detail"
                className="rounded-2xl shadow-lg h-72 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1627359212239-3d82a215af75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxtb2Rlc3QlMjBmYXNoaW9ufGVufDB8fHx8MTc3MDYyNjc3NXww&ixlib=rb-4.1.0&q=85"
                alt="Hijab Style"
                className="rounded-2xl shadow-lg h-72 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-[#faf8f6]">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#6b5d52] mb-4">What Our Customers Say</h2>
            <p className="text-[#8b7968]">Read testimonials from our satisfied customers</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-[#e8ddd2] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#f4c27d] text-[#f4c27d]" />
                    ))}
                  </div>
                  <p className="text-[#6b5d52] mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t border-[#e8ddd2] pt-4">
                    <p className="font-semibold text-[#6b5d52]">{testimonial.name}</p>
                    <p className="text-sm text-[#a89580]">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl font-bold text-[#6b5d52] mb-4">Get in Touch</h2>
                <p className="text-[#8b7968]">
                  Have questions or want to place an order? We'd love to hear from you!
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#f4e4d7] p-3 rounded-lg">
                    <Phone className="w-5 h-5 text-[#8b7355]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#6b5d52] mb-1">Phone</p>
                    <p className="text-[#8b7968]">{shopInfo.phone}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-[#f4e4d7] p-3 rounded-lg">
                    <Mail className="w-5 h-5 text-[#8b7355]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#6b5d52] mb-1">Email</p>
                    <p className="text-[#8b7968]">{shopInfo.email}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-[#f4e4d7] p-3 rounded-lg">
                    <MapPin className="w-5 h-5 text-[#8b7355]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#6b5d52] mb-1">Location</p>
                    <p className="text-[#8b7968]">{shopInfo.address}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-[#f4e4d7] p-3 rounded-lg">
                    <Clock className="w-5 h-5 text-[#8b7355]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#6b5d52] mb-1">Hours</p>
                    <p className="text-[#8b7968]">{shopInfo.hours}</p>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <Button 
                  onClick={handleWhatsApp}
                  className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white"
                  size="lg"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Contact via WhatsApp
                </Button>
              </div>
            </div>

            <Card className="border-[#e8ddd2]">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#6b5d52] mb-6">Send us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#6b5d52] mb-2">Name</label>
                    <Input 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                      className="border-[#e8ddd2] focus:border-[#c9a68a]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#6b5d52] mb-2">Email</label>
                    <Input 
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                      className="border-[#e8ddd2] focus:border-[#c9a68a]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#6b5d52] mb-2">Message</label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="How can we help you?"
                      rows={5}
                      required
                      className="border-[#e8ddd2] focus:border-[#c9a68a]"
                    />
                  </div>
                  <Button 
                    type="submit"
                    className="w-full bg-[#8b7355] hover:bg-[#6b5d52] text-white"
                    size="lg"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#6b5d52] text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_0f087157-7047-42d2-963e-36ed51ca707e/artifacts/qd2g138c_Shop%20Cover%20Photo.jpeg" 
                  alt="Zaya Logo" 
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold">Zaya</h3>
                  <p className="text-sm text-[#c9a68a]">{shopInfo.tagline}</p>
                </div>
              </div>
              <p className="text-sm text-white/80">
                Your destination for elegant and modest fashion.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li><a href="#home" className="hover:text-[#c9a68a] transition-colors">Home</a></li>
                <li><a href="#collections" className="hover:text-[#c9a68a] transition-colors">Collections</a></li>
                <li><a href="#about" className="hover:text-[#c9a68a] transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-[#c9a68a] transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Follow Us</h4>
              <div className="flex gap-4">
                <a href="#" className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-colors">
                  <Phone className="w-5 h-5" />
                </a>
              </div>
              <p className="text-sm text-white/80 mt-4">{shopInfo.social.instagram}</p>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-8 text-center text-sm text-white/60">
            <p>&copy; 2024 Zaya Abaya & Hijab. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
